import { useEffect, useState } from 'react';

import { api } from '../api/client';
import type { TaxCode } from '../types';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

// Placeholder tax summary data
const taxSummary = {
  period: { start: '2026-01-01', end: '2026-03-31' },
  gst: {
    collected: 4500.00,
    paid: 1250.00,
    net: 3250.00,
  },
  hst: {
    ontario: {
      collected: 8900.00,
      paid: 2100.00,
      net: 6800.00,
    },
  },
  qst: {
    collected: 0,
    paid: 0,
    net: 0,
  },
  pst: {
    bc: { collected: 0, paid: 0, net: 0 },
    sk: { collected: 0, paid: 0, net: 0 },
    mb: { collected: 0, paid: 0, net: 0 },
  },
};

export function TaxSummary() {
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchTaxCodes() {
      try {
        const data = await api.taxCodes.list();
        setTaxCodes(data);
      } catch (error) {
        console.error('Failed to fetch tax codes:', error);
      } finally {
        setLoading(false);
      }
    }
    void fetchTaxCodes();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading tax summary...</p>
      </div>
    );
  }

  const totalRemittance = taxSummary.gst.net + taxSummary.hst.ontario.net;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Tax Summary</h1>
          <p className="text-sm text-muted-foreground">Sales tax collected and paid</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            defaultValue={taxSummary.period.start}
          />
          <span className="text-muted-foreground">to</span>
          <input
            type="date"
            className="px-3 py-2 text-sm border border-input rounded-md bg-background"
            defaultValue={taxSummary.period.end}
          />
          <Button variant="outline" size="sm">Export CSV</Button>
          <Button variant="default" size="sm">Print Report</Button>
        </div>
      </div>

      <p className="text-sm text-muted-foreground">
        For the period {formatDate(taxSummary.period.start)} to {formatDate(taxSummary.period.end)}
      </p>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>GST/HST Collected</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency(taxSummary.gst.collected + taxSummary.hst.ontario.collected)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>GST/HST Paid (ITC)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-red-600">
              {formatCurrency(taxSummary.gst.paid + taxSummary.hst.ontario.paid)}
            </div>
          </CardContent>
        </Card>
        <Card className="border-primary">
          <CardHeader className="pb-2">
            <CardDescription>Net Tax Remittance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold font-mono ${totalRemittance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(Math.abs(totalRemittance))}
              {totalRemittance >= 0 ? ' owed' : ' refund'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* GST Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-percent"></i> GST (5%)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">Description</th>
                <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border hover:bg-muted/50">
                <td className="py-2 px-3">GST Collected (Sales)</td>
                <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(taxSummary.gst.collected)}</td>
              </tr>
              <tr className="border-b border-border hover:bg-muted/50">
                <td className="py-2 px-3">GST Paid (Purchases - ITC)</td>
                <td className="py-2 px-3 text-right font-mono text-red-600">({formatCurrency(taxSummary.gst.paid)})</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="font-semibold bg-muted/50">
                <td className="py-2 px-3">Net GST</td>
                <td className="py-2 px-3 text-right font-mono">{formatCurrency(taxSummary.gst.net)}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </CardContent>
      </Card>

      {/* HST Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-percent"></i> HST (13% Ontario)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 px-3 text-left font-medium text-muted-foreground">Description</th>
                <th className="py-2 px-3 text-right font-medium text-muted-foreground">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-border hover:bg-muted/50">
                <td className="py-2 px-3">HST Collected (Sales)</td>
                <td className="py-2 px-3 text-right font-mono text-green-600">{formatCurrency(taxSummary.hst.ontario.collected)}</td>
              </tr>
              <tr className="border-b border-border hover:bg-muted/50">
                <td className="py-2 px-3">HST Paid (Purchases - ITC)</td>
                <td className="py-2 px-3 text-right font-mono text-red-600">({formatCurrency(taxSummary.hst.ontario.paid)})</td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="font-semibold bg-muted/50">
                <td className="py-2 px-3">Net HST (Ontario)</td>
                <td className="py-2 px-3 text-right font-mono">{formatCurrency(taxSummary.hst.ontario.net)}</td>
              </tr>
            </tfoot>
          </table>
          </div>
        </CardContent>
      </Card>

      {/* Total Remittance */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Total GST/HST Remittance</h3>
              <p className="text-sm text-muted-foreground">Amount to be remitted to CRA</p>
            </div>
            <div className="text-2xl font-bold font-mono">
              {formatCurrency(totalRemittance)}
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" size="sm">View Filing History</Button>
            <Button variant="default" size="sm">Generate Return</Button>
          </div>
        </CardContent>
      </Card>

      {/* Tax Codes Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-list-ul"></i> Active Tax Codes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Code</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Name</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Rate</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Type</th>
                  <th className="py-2 px-3 text-left font-medium text-muted-foreground">Province</th>
                </tr>
              </thead>
              <tbody>
                {taxCodes.filter(tc => tc.is_active).map((taxCode) => (
                  <tr key={taxCode.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-2 px-3"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{taxCode.code}</code></td>
                    <td className="py-2 px-3">{taxCode.name}</td>
                    <td className="py-2 px-3 font-mono">{(taxCode.rate * 100).toFixed(1)}%</td>
                    <td className="py-2 px-3">
                      <Badge variant={taxCode.type === 'HST' ? 'default' : taxCode.type === 'GST' ? 'secondary' : 'default'}>
                        {taxCode.type}
                      </Badge>
                    </td>
                    <td className="py-2 px-3">{taxCode.province ?? 'All'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}